<template>
  <v-dialog v-model="dialog" max-width="400" scrollable>
    <v-card class="ccm-card">
      <!-- Header -->
      <div class="ccm-header">
        <div class="ccm-header__left">
          <v-icon color="#F59E0B" size="20">mdi-star-circle</v-icon>
          <span class="ccm-header__title">{{ isConversion ? 'รายละเอียดการแลกเงิน' : 'สายค่าคอมมิชชั่น' }}</span>
          <span v-if="data" class="ccm-header__label">{{ data.chainLabelTh }}</span>
        </div>
        <v-btn icon small @click="dialog = false">
          <v-icon size="18">mdi-close</v-icon>
        </v-btn>
      </div>

      <!-- Amount -->
      <div v-if="data" class="ccm-amount">
        <span class="ccm-amount__value" :class="{ 'ccm-amount--negative': data.amount < 0 }">
          {{ data.amount > 0 ? '+' : '' }}{{ formatNum(data.amount) }}
        </span>
        <span class="ccm-amount__unit" :class="{ 'ccm-amount--negative': data.amount < 0 }">Coins</span>
        <span v-if="isConversion && data.amount < 0" class="ccm-amount__sub">ที่คุณทำรายการ</span>
        <span v-else class="ccm-amount__sub">ที่คุณได้รับ {{ data.myGeneration ? `(G${data.myGeneration})` : '' }}</span>
      </div>

      <!-- Loading -->
      <div v-if="loading" class="ccm-loading">
        <v-progress-circular indeterminate color="#F59E0B" size="32" />
        <div class="ccm-loading__text">กำลังโหลด...</div>
      </div>

      <!-- Error -->
      <div v-else-if="error" class="ccm-error">
        <v-icon color="#EF4444">mdi-alert-circle-outline</v-icon>
        <span>{{ error }}</span>
      </div>

      <!-- Chain -->
      <div v-else-if="data" class="ccm-chain">
        <!-- referrer at top (index = last in array) shown first in reverse -->
        <template v-for="(node, idx) in reversedChain">
          <!-- connector line above (except first) -->
          <div v-if="idx > 0" :key="'line-' + idx" class="ccm-connector">
            <div class="ccm-connector__line" />
          </div>

          <div
            :key="'node-' + idx"
            class="ccm-node"
            :class="{
              'ccm-node--me': node.isMe,
              'ccm-node--referrer': node.isReferrer,
              'ccm-node--originator': node.isOriginator,
            }"
          >
            <!-- Avatar -->
            <div class="ccm-node__avatar" :class="avatarClass(node)">
              <v-icon size="18" :color="avatarIconColor(node)">{{ avatarIcon(node) }}</v-icon>
            </div>

            <!-- Info -->
            <div class="ccm-node__info">
              <div class="ccm-node__name">{{ node.displayName }}</div>
              <div class="ccm-node__badge" :class="badgeClass(node)">{{ node.label }}</div>
            </div>

            <!-- My marker -->
            <div v-if="node.isMe" class="ccm-node__you-tag">
              <v-icon size="12" color="#fff">mdi-account-check</v-icon>
              คุณ
            </div>
          </div>
        </template>

        <!-- Hidden upline hint -->
        <div class="ccm-connector">
          <div class="ccm-connector__line ccm-connector__line--dashed" />
        </div>
        <div class="ccm-hidden-hint">
          <v-icon size="14" color="#9CA3AF">mdi-dots-vertical</v-icon>
          <span>upline ถัดไปถูกซ่อน (privacy)</span>
        </div>
      </div>

      <div class="ccm-footer">
        <v-btn depressed block color="#F59E0B" dark @click="dialog = false">ปิด</v-btn>
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
import WalletService from "@/services/api/wallet";

export default {
  name: "CommissionChainModal",
  props: {
    value: Boolean,
    transaction: { type: Object, default: null },
    role: { type: String, default: "user" },
  },
  data() {
    return {
      loading: false,
      error: null,
      data: null,
    };
  },
  computed: {
    dialog: {
      get() { return this.value; },
      set(v) { this.$emit("input", v); },
    },
    reversedChain() {
      if (!this.data?.chain) return [];
      return [...this.data.chain].reverse();
    },
    isConversion() {
      return this.data?.chainLabel === "conversion";
    },
  },
  watch: {
    value(v) {
      if (v && this.transaction) this.load();
      if (!v) this.data = null;
    },
  },
  methods: {
    async load() {
      this.loading = true;
      this.error = null;
      this.data = null;
      try {
        const res = await WalletService.getTransactionChain(this.transaction.id, this.role);
        this.data = res.data;
      } catch (e) {
        this.error = "ไม่สามารถโหลดข้อมูล chain ได้";
      } finally {
        this.loading = false;
      }
    },
    formatNum(n) {
      return Number(n || 0).toLocaleString("th-TH", { minimumFractionDigits: 0, maximumFractionDigits: 2 });
    },
    avatarIcon(node) {
      if (node.kind === "seller") return "mdi-store";
      if (node.kind === "rider") return "mdi-motorbike";
      return "mdi-account";
    },
    avatarIconColor(node) {
      if (node.isMe) return "#fff";
      if (node.isOriginator) return "#3B82F6";
      if (node.isReferrer) return "#8B5CF6";
      return "#6B7280";
    },
    avatarClass(node) {
      if (node.isMe) return "ccm-avatar--me";
      if (node.isOriginator) return "ccm-avatar--originator";
      if (node.isReferrer) return "ccm-avatar--referrer";
      return "ccm-avatar--default";
    },
    badgeClass(node) {
      if (node.isMe) return "ccm-badge--me";
      if (node.isOriginator) return "ccm-badge--originator";
      if (node.isReferrer) return "ccm-badge--referrer";
      return "ccm-badge--default";
    },
  },
};
</script>

<style scoped>
.ccm-card { border-radius: 16px !important; overflow: hidden; }

.ccm-header {
  display: flex; align-items: center; justify-content: space-between;
  padding: 14px 16px 10px;
  border-bottom: 1px solid #F3F4F6;
}
.ccm-header__left { display: flex; align-items: center; gap: 6px; }
.ccm-header__title { font-size: 15px; font-weight: 700; color: #111827; }
.ccm-header__label {
  font-size: 11px; background: #FEF3C7; color: #92400E;
  padding: 2px 8px; border-radius: 20px;
}

.ccm-amount {
  display: flex; align-items: baseline; gap: 4px;
  padding: 12px 16px 4px;
}
.ccm-amount__value { font-size: 26px; font-weight: 800; color: #F59E0B; }
.ccm-amount__value.ccm-amount--negative { color: #EF4444; }
.ccm-amount__unit { font-size: 13px; color: #F59E0B; font-weight: 600; }
.ccm-amount__unit.ccm-amount--negative { color: #EF4444; }
.ccm-amount__sub { font-size: 12px; color: #9CA3AF; margin-left: 4px; }

.ccm-loading, .ccm-error {
  display: flex; flex-direction: column; align-items: center;
  gap: 8px; padding: 32px 16px; color: #6B7280; font-size: 13px;
}
.ccm-error { flex-direction: row; }

.ccm-chain { padding: 12px 16px 0; }

.ccm-node {
  display: flex; align-items: center; gap: 10px;
  padding: 10px 12px; border-radius: 12px;
  background: #F9FAFB; border: 1.5px solid #E5E7EB;
  position: relative;
}
.ccm-node--me {
  background: #FFFBEB; border-color: #F59E0B;
  box-shadow: 0 0 0 3px #FEF3C7;
}
.ccm-node--referrer { background: #F5F3FF; border-color: #8B5CF6; }
.ccm-node--originator { background: #EFF6FF; border-color: #3B82F6; }

.ccm-node__avatar {
  width: 36px; height: 36px; border-radius: 50%;
  display: flex; align-items: center; justify-content: center; flex-shrink: 0;
}
.ccm-avatar--me { background: #F59E0B; }
.ccm-avatar--originator { background: #DBEAFE; }
.ccm-avatar--referrer { background: #EDE9FE; }
.ccm-avatar--default { background: #E5E7EB; }

.ccm-node__info { flex: 1; min-width: 0; }
.ccm-node__name { font-size: 14px; font-weight: 600; color: #111827; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.ccm-node__badge {
  display: inline-block; margin-top: 2px;
  font-size: 10px; font-weight: 600; padding: 1px 7px; border-radius: 20px;
}
.ccm-badge--me { background: #FEF3C7; color: #92400E; }
.ccm-badge--originator { background: #DBEAFE; color: #1D4ED8; }
.ccm-badge--referrer { background: #EDE9FE; color: #6D28D9; }
.ccm-badge--default { background: #F3F4F6; color: #6B7280; }

.ccm-node__you-tag {
  display: flex; align-items: center; gap: 3px;
  font-size: 10px; font-weight: 700; color: #fff;
  background: #F59E0B; padding: 2px 7px; border-radius: 20px;
  flex-shrink: 0;
}

.ccm-connector {
  display: flex; justify-content: center;
  height: 18px; align-items: center;
}
.ccm-connector__line {
  width: 2px; height: 100%; background: #E5E7EB;
}
.ccm-connector__line--dashed {
  background: repeating-linear-gradient(to bottom, #E5E7EB 0px, #E5E7EB 4px, transparent 4px, transparent 8px);
}

.ccm-hidden-hint {
  display: flex; align-items: center; gap: 4px; justify-content: center;
  font-size: 11px; color: #9CA3AF; padding: 4px 0 12px;
}

.ccm-footer { padding: 12px 16px 16px; }
</style>
